import React, { useState } from "react";
import { type PillarTableVirtualRow } from "shared/components/pillar-table/hooks/usePillarTableVirtualValues";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";

export type PillarTableRowContextType<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  rowValue: RowGeneric;
  setRowValue: React.Dispatch<React.SetStateAction<RowGeneric>>;
  rowIndex: number;
  rowExpanded: "open" | "close" | "none";
  setRowExpanded: React.Dispatch<
    React.SetStateAction<"open" | "close" | "none">
  >;
  inlineEditMode: "none" | "edit" | "delete" | "saving" | "deleting";
  formValues: InlineEditFormGeneric;
  setFormValues: React.Dispatch<React.SetStateAction<InlineEditFormGeneric>>;
  setInlineEditMode: React.Dispatch<
    React.SetStateAction<"none" | "edit" | "saving" | "delete" | "deleting">
  >;
  // This parameter is used in the virtualization logic to render hidden elements for determining table column widths
  virtualRowValue?: PillarTableVirtualRow<RowGeneric>;
};

export const PillarTableRowContext =
  React.createContext<PillarTableRowContextType<
    PillarTableSafeAny,
    PillarTableSafeAny,
    PillarTableSafeAny
  > | null>(null);

export const usePillarTableRowContext = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>(): PillarTableRowContextType<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> => {
  const context = React.useContext(PillarTableRowContext);
  if (!context) {
    throw new Error(
      "usePillarTableRowContext must be used within a PillarTableRowContext.Provider",
    );
  }
  return context as PillarTableRowContextType<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >;
};

export const PillarTableRowContextProvider = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  initRowValue,
  rowIndex,
  virtualRowValue,
  ...props
}: React.PropsWithChildren<{
  rowIndex: number;
  initRowValue: RowGeneric;
  virtualRowValue?: PillarTableVirtualRow<RowGeneric>;
}>) => {
  const [rowExpanded, setRowExpanded] = useState<"open" | "close" | "none">(
    "none",
  );
  const [rowValue, setRowValue] = useState<RowGeneric>(initRowValue);
  const [formValues, setFormValues] = useState<InlineEditFormGeneric>(
    {} as InlineEditFormGeneric,
  );
  const [inlineEditMode, setInlineEditMode] = useState<
    "none" | "edit" | "saving" | "delete" | "deleting"
  >("none");
  return (
    <PillarTableRowContext.Provider
      value={{
        rowValue: rowValue,
        setRowValue: setRowValue,
        rowIndex: rowIndex,
        rowExpanded: rowExpanded,
        setRowExpanded: setRowExpanded,
        inlineEditMode: inlineEditMode,
        setInlineEditMode: setInlineEditMode,
        formValues: formValues,
        setFormValues: setFormValues,
        virtualRowValue: virtualRowValue,
      }}
    >
      {props.children}
    </PillarTableRowContext.Provider>
  );
};
